import { stripNovaResponseGuidance } from './nova-response-contract';

export interface WebsiteReportReference {
  id: string; name: string; scanDate: string; legacy: boolean;
  selection?: 'latest completed accessible website report by scan date, checked for this request';
}
const OPEN = '[APEX WEBSITE REPORT REFERENCE v1]';
const CLOSE = '[END APEX WEBSITE REPORT REFERENCE]';
const validId = (id: unknown): id is string => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,200}$/.test(id);

export function withoutServerContext(content: string): string {
  if (!content.startsWith('[APEX SYSTEM CONTEXT]')) return stripNovaResponseGuidance(content);
  const end = content.indexOf('[END APEX SYSTEM CONTEXT]');
  return end < 0 ? content : stripNovaResponseGuidance(content.slice(end + '[END APEX SYSTEM CONTEXT]'.length).trim());
}

export function parseWebsiteReportReference(raw: string): { reference: WebsiteReportReference; text: string } | null {
  const content = withoutServerContext(raw);
  if (!content.startsWith(OPEN + '\n')) return null;
  const end = content.indexOf('\n' + CLOSE);
  if (end < 0 || end > 2000) return null;
  try {
    const reference: WebsiteReportReference = JSON.parse(content.slice(OPEN.length + 1, end));
    if (!validId(reference.id) || typeof reference.name !== 'string' || reference.name.length > 160
      || typeof reference.scanDate !== 'string' || !Number.isFinite(Date.parse(reference.scanDate))
      || typeof reference.legacy !== 'boolean'
      || (reference.selection !== undefined && reference.selection !== 'latest completed accessible website report by scan date, checked for this request')) return null;
    return { reference, text: content.slice(end + CLOSE.length + 1).trim() };
  } catch { return null; }
}

export function appendWebsiteReportReference(content: string, reference: WebsiteReportReference): string {
  return `${OPEN}\n${JSON.stringify(reference).replace(/</g, '\\u003c')}\n${CLOSE}\n\n${content}`;
}

export function historicalWebsiteReportId(content: string): { id: string; legacy: boolean } | null {
  if (!content.startsWith('[APEX SYSTEM CONTEXT]')) return null;
  const end = content.indexOf('[END APEX SYSTEM CONTEXT]');
  if (end < 0) return null;
  const block = content.slice(0, end);
  const type = block.match(/^The report\/project type is: (website(?:-legacy)?)\s*$/m)?.[1];
  const id = block.match(/^The active scan project id is: ([^\r\n]+)$/m)?.[1]?.trim();
  return type && validId(id) ? { id, legacy: type === 'website-legacy' } : null;
}

export const reportReferencePath = (reference: { id: string; legacy: boolean }) =>
  '/dashboard/website-scan/' + (reference.legacy ? 'legacy/' : '') + encodeURIComponent(reference.id);
